import React, { useState, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    ButtonGroup,
    Button,
    Tooltip,
    Modal,
    Card,
    CardContent,
    Chip,
    useTheme,
    useMediaQuery,
    Fade,
    Slide,
    ThemeProvider,
    createTheme,
    Divider,
    Avatar
  } from '@mui/material';
  import {
    ChevronLeft,
    ChevronRight,
    CalendarToday,
    GridView,
    ViewColumn,
    CropSquare,
    Add,
    Edit,
    Delete,
    AccessTime,
    LocationOn,
    Settings,
    Search,
    MoreVert
  } from '@mui/icons-material';
  
  // Enhanced Google Calendar theme
  const googleCalendarTheme = createTheme({
    palette: {
      primary: {
        main: '#1a73e8',
        light: '#e8f0fe',
        dark: '#1557b0',
      },
      secondary: {
        main: '#34a853',
        light: '#e6f4ea',
      },
      background: {
        default: '#ffffff',
        paper: '#ffffff',
      },
      grey: {
        50: '#f8f9fa',
        100: '#f1f3f4',
        200: '#e8eaed',
        300: '#dadce0',
        400: '#bdc1c6',
        500: '#9aa0a6',
        600: '#80868b',
        700: '#5f6368',
        800: '#3c4043',
        900: '#202124',
      },
      text: {
        primary: '#3c4043',
        secondary: '#5f6368',
      },
    },
    typography: {
      fontFamily: '"Google Sans", "Roboto", "Arial", sans-serif',
      h4: {
        fontWeight: 400,
        fontSize: '1.5rem',
      },
      h6: {
        fontWeight: 500,
        fontSize: '1.125rem',
      },
      body1: {
        fontSize: '0.875rem',
      },
      body2: {
        fontSize: '0.75rem',
      },
      caption: {
        fontSize: '0.6875rem',
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 24,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
    },
  });
  
  // Sample events data with enhanced properties
  const sampleEvents = [
    {
      id: 1,
      title: 'Team Standup',
      start: new Date(2025, 5, 13, 9, 0),
      end: new Date(2025, 5, 13, 9, 30),
      color: '#1a73e8',
      backgroundColor: '#e8f0fe',
      location: 'Conference Room A',
      description: 'Daily team sync meeting to discuss progress and blockers',
      attendees: ['john@company.com', 'sarah@company.com'],
      type: 'meeting'
    },
    {
      id: 2,
      title: 'Project Review',
      start: new Date(2025, 5, 13, 14, 0),
      end: new Date(2025, 5, 13, 15, 30),
      color: '#34a853',
      backgroundColor: '#e6f4ea',
      location: 'Online - Meet',
      description: 'Q2 project milestone review with stakeholders',
      attendees: ['manager@company.com', 'client@external.com'],
      type: 'meeting'
    },
    {
      id: 3,
      title: 'Client Call',
      start: new Date(2025, 5, 14, 11, 0),
      end: new Date(2025, 5, 14, 12, 0),
      color: '#ff6d01',
      backgroundColor: '#fef7e0',
      location: 'Phone',
      description: 'Monthly client check-in and feedback session',
      attendees: ['client@external.com'],
      type: 'call'
    },
    {
      id: 4,
      title: 'Design Workshop',
      start: new Date(2025, 5, 15, 10, 0),
      end: new Date(2025, 5, 15, 16, 0),
      color: '#9c27b0',
      backgroundColor: '#f3e5f5',
      location: 'Design Studio',
      description: 'UX/UI design thinking session for new product features',
      attendees: ['design-team@company.com'],
      type: 'workshop'
    },
    {
      id: 5,
      title: 'All Hands Meeting',
      start: new Date(2025, 5, 16, 15, 0),
      end: new Date(2025, 5, 16, 16, 0),
      color: '#d32f2f',
      backgroundColor: '#ffebee',
      location: 'Main Auditorium',
      description: 'Monthly company-wide meeting with leadership updates',
      attendees: ['all-hands@company.com'],
      type: 'meeting'
    }
  ];
  
  // Custom Toolbar Component
  const CustomToolbar = forwardRef(({ onViewChange, currentView }, ref) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    
    useImperativeHandle(ref, () => ({
      getApi: () => ({
        next: () => navigateDate(1),
        prev: () => navigateDate(-1),
        today: () => goToToday(),
        getDate: () => currentDate,
      })
    }));
  
    const navigateDate = (direction) => {
      const newDate = new Date(currentDate);
      if (currentView === 'month') {
        newDate.setMonth(currentDate.getMonth() + direction);
      } else if (currentView === 'week') {
        newDate.setDate(currentDate.getDate() + (direction * 7));
      } else {
        newDate.setDate(currentDate.getDate() + direction);
      }
      setCurrentDate(newDate);
    };
  
    const goToToday = () => {
      setCurrentDate(new Date());
    };
  
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
          mb: 2,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={() => navigateDate(-1)} 
            size="small"
            sx={{ 
              '&:hover': { backgroundColor: 'grey.100' },
              color: 'text.primary'
            }}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>
          <IconButton 
            onClick={() => navigateDate(1)} 
            size="small"
            sx={{ 
              '&:hover': { backgroundColor: 'grey.100' },
              color: 'text.primary'
            }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
          <Button
            variant="outlined"
            size="small"
            onClick={goToToday}
            sx={{ 
              ml: 1,
              borderColor: 'grey.300',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'grey.400',
                backgroundColor: 'grey.50'
              }
            }}
          >
            Today
          </Button>
          <Typography variant="h6" sx={{ ml: 2, fontWeight: 400, color: 'text.primary' }}>
            {currentDate.toLocaleDateString('en-US', {
              month: 'long',
              year: 'numeric',
            })}
          </Typography>
        </Box>
  
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ButtonGroup size="small" variant="outlined">
            <Button
              variant={currentView === 'month' ? 'contained' : 'outlined'}
              onClick={() => onViewChange('month')}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              Month
            </Button>
            <Button
              variant={currentView === 'week' ? 'contained' : 'outlined'}
              onClick={() => onViewChange('week')}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              Week
            </Button>
            <Button
              variant={currentView === 'day' ? 'contained' : 'outlined'}
              onClick={() => onViewChange('day')}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              Day
            </Button>
          </ButtonGroup>
          
          <IconButton size="small" sx={{ ml: 1, color: 'text.secondary' }}>
            <Search fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={{ color: 'text.secondary' }}>
            <Settings fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  });
  
  // Enhanced Event Content Component
  const EventContent = ({ event }) => {
    const duration = (event.end - event.start) / (1000 * 60); // duration in minutes
    const isShort = duration < 60;
  
    return (
      <Box
        sx={{
          p: 0.5,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          cursor: 'pointer',
        }}
      >
        <Typography 
          variant="caption" 
          sx={{ 
            fontWeight: 500,
            fontSize: '0.75rem',
            lineHeight: 1.2,
            color: 'inherit',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: isShort ? 'nowrap' : 'normal',
            display: '-webkit-box',
            WebkitLineClamp: isShort ? 1 : 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {event.title}
        </Typography>
        {!isShort && (
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.6875rem',
              opacity: 0.8,
              mt: 0.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {event.start.toLocaleTimeString('en-US', { 
              hour: 'numeric', 
              minute: '2-digit', 
              hour12: true 
            })}
            {event.location && ` • ${event.location}`}
          </Typography>
        )}
      </Box>
    );
  };
  
  // Main Google Calendar Component
  const GoogleCalendar = ({
    handleEventClick,
    renderEventContent,
    weekendsVisible = true,
    currentEvents = sampleEvents,
  }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [currentDate, setCurrentDate] = useState(new Date());
    const [view, setView] = useState('week');
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [events] = useState(currentEvents);
    const calendarRef = useRef(null);
  
    // Generate time slots for day/week view
    const timeSlots = useMemo(() => {
      const slots = [];
      for (let hour = 0; hour < 24; hour++) {
        slots.push({
          time: `${hour.toString().padStart(2, '0')}:00`,
          hour24: hour,
          hour12: hour === 0 ? 12 : hour > 12 ? hour - 12 : hour,
          period: hour < 12 ? 'AM' : 'PM'
        });
      }
      return slots;
    }, []);
  
    // Get current week dates
    const weekDates = useMemo(() => {
      const startOfWeek = new Date(currentDate);
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day;
      startOfWeek.setDate(diff);
      
      const dates = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        dates.push(date);
      }
      return dates;
    }, [currentDate]);
  
    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    };
  
    const isToday = (date) => {
      const today = new Date();
      return date.toDateString() === today.toDateString();
    };
  
    const getCurrentTime = () => {
      const now = new Date();
      return (now.getHours() + now.getMinutes() / 60) * 60; // Position in pixels
    };
  
    const getEventsForDate = (date) => {
      return events.filter(event => 
        event.start.toDateString() === date.toDateString()
      );
    };
  
    const getEventPosition = (event) => {
      const startHour = event.start.getHours();
      const startMinutes = event.start.getMinutes();
      const endHour = event.end.getHours();
      const endMinutes = event.end.getMinutes();
      
      const top = (startHour + startMinutes / 60) * 60; // 60px per hour
      const height = Math.max(((endHour + endMinutes / 60) - (startHour + startMinutes / 60)) * 60, 24);
      
      return { top, height };
    };
  
    const handleInternalEventClick = (event) => {
      setSelectedEvent(event);
      if (handleEventClick) {
        handleEventClick({ event });
      }
    };
  
    const handleCloseModal = () => {
      setSelectedEvent(null);
    };
  
    const navigateDate = (direction) => {
      const newDate = new Date(currentDate);
      if (view === 'month') {
        newDate.setMonth(currentDate.getMonth() + direction);
      } else if (view === 'week') {
        newDate.setDate(currentDate.getDate() + (direction * 7));
      } else {
        newDate.setDate(currentDate.getDate() + direction);
      }
      setCurrentDate(newDate);
    };
  
    const goToToday = () => {
      setCurrentDate(new Date());
    };
  
    const EventCard = ({ event, style = {} }) => (
      <Fade in timeout={300}>
        <Paper
          elevation={0}
          onClick={() => handleInternalEventClick(event)}
          sx={{
            position: 'absolute',
            left: 4,
            right: 4,
            backgroundColor: event.backgroundColor || event.color,
            border: `1px solid ${event.color}`,
            borderLeftWidth: 3,
            color: event.color,
            cursor: 'pointer',
            borderRadius: 1,
            overflow: 'hidden',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: `0 2px 8px ${event.color}40`,
              borderLeftWidth: 4,
            },
            ...style
          }}
        >
          {renderEventContent ? 
            renderEventContent({ event }) : 
            <EventContent event={event} />
          }
        </Paper>
      </Fade>
    );
  
    // Current time indicator
    const TimeIndicator = () => {
      const now = new Date();
      const currentTimePosition = getCurrentTime();
      const isCurrentDay = weekDates.some(date => isToday(date));
      
      if (!isCurrentDay || view !== 'week') return null;
  
      return (
        <Box
          sx={{
            position: 'absolute',
            top: currentTimePosition,
            left: 0,
            right: 0,
            height: 2,
            backgroundColor: 'error.main',
            zIndex: 10,
            '&::before': {
              content: '""',
              position: 'absolute',
              left: -6,
              top: -5,
              width: 12,
              height: 12,
              backgroundColor: 'error.main',
              borderRadius: '50%',
            }
          }}
        />
      );
    };
  
    const WeekView = () => (
      <Box sx={{ display: 'flex', height: '100%', backgroundColor: 'background.paper' }}>
        {/* Time column */}
        <Box sx={{ 
          width: 80, 
          borderRight: 1, 
          borderColor: 'grey.200', 
          backgroundColor: 'grey.50',
          position: 'sticky',
          left: 0,
          zIndex: 5,
        }}>
          <Box sx={{ height: 60 }} />
          {timeSlots.map((slot, index) => (
            <Box
              key={slot.time}
              sx={{
                height: 60,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                pt: index === 0 ? 0 : 1,
                borderTop: index > 0 ? 1 : 0,
                borderColor: 'grey.200',
                position: 'relative',
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.6875rem',
                  fontWeight: 400,
                }}
              >
                {slot.hour12 === 12 ? '12' : slot.hour12}{slot.period}
              </Typography>
            </Box>
          ))}
        </Box>
  
        {/* Days columns */}
        <Box sx={{ flex: 1, display: 'flex', position: 'relative' }}>
          {weekDates.map((date, dayIndex) => (
            <Box
              key={date.toDateString()}
              sx={{
                flex: 1,
                borderRight: dayIndex < 6 ? 1 : 0,
                borderColor: 'grey.200',
                position: 'relative',
                minWidth: 0,
              }}
            >
              {/* Day header */}
              <Box
                sx={{
                  height: 60,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderBottom: 1,
                  borderColor: 'grey.200',
                  backgroundColor: isToday(date) ? 'primary.light' : 'grey.50',
                  position: 'sticky',
                  top: 0,
                  zIndex: 3,
                }}
              >
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: '0.6875rem',
                    fontWeight: 500,
                    letterSpacing: '0.5px',
                  }}
                >
                  {date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: isToday(date) ? 'primary.main' : 'text.primary',
                    fontWeight: isToday(date) ? 600 : 400,
                    fontSize: '1.125rem',
                  }}
                >
                  {date.getDate()}
                </Typography>
              </Box>
  
              {/* Time grid */}
              <Box sx={{ position: 'relative', height: 60 * 24 }}>
                {timeSlots.map((slot, index) => (
                  <Box
                    key={slot.time}
                    sx={{
                      height: 60,
                      borderTop: index > 0 ? 1 : 0,
                      borderColor: 'grey.200',
                      '&:hover': {
                        backgroundColor: 'grey.50',
                      },
                    }}
                  />
                ))}
  
                {/* Current time indicator for today */}
                {isToday(date) && <TimeIndicator />}
  
                {/* Events */}
                {getEventsForDate(date).map((event) => {
                  const position = getEventPosition(event);
                  return (
                    <EventCard
                      key={event.id}
                      event={event}
                      style={{
                        top: position.top,
                        height: position.height,
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  
    const MobileAgendaView = () => (
      <Box sx={{ p: 2 }}>
        {weekDates.map((date) => {
          const dayEvents = getEventsForDate(date);
          return (
            <Card key={date.toDateString()} sx={{ mb: 2, overflow: 'visible', boxShadow: 1 }}>
              <CardContent sx={{ pb: '16px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: dayEvents.length > 0 ? 2 : 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: isToday(date) ? 'primary.main' : 'text.primary',
                      fontWeight: isToday(date) ? 600 : 500,
                    }}
                  >
                    {formatDate(date)}
                  </Typography>
                  {isToday(date) && (
                    <Chip
                      label="Today"
                      size="small"
                      color="primary"
                      sx={{ ml: 1, height: 20 }}
                    />
                  )}
                </Box>
                
                {dayEvents.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No events
                  </Typography>
                ) : (
                  dayEvents.map((event) => (
                    <Paper
                      key={event.id}
                      elevation={0}
                      onClick={() => handleInternalEventClick(event)}
                      sx={{
                        p: 2,
                        mb: 1,
                        cursor: 'pointer',
                        borderLeft: 4,
                        borderColor: event.color,
                        backgroundColor: event.backgroundColor || 'grey.50',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateX(4px)',
                          boxShadow: 2,
                        },
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {event.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <AccessTime fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {event.start.toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit', 
                            hour12: true 
                          })} - {event.end.toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit', 
                            hour12: true 
                          })}
                        </Typography>
                      </Box>
                      {event.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                          <LocationOn fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {event.location}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  ))
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  
    return (
      <ThemeProvider theme={googleCalendarTheme}>
        <Paper
          elevation={0}
          sx={{
            height: '100vh',
            maxHeight: '800px',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'background.default',
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          {/* Header */}
          <Box sx={{ p: 3, backgroundColor: 'background.paper' }}>
            <CustomToolbar 
              ref={calendarRef}
              onViewChange={setView}
              currentView={view}
            />
          </Box>
  
          {/* Calendar Content */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Slide direction="left" in mountOnEnter unmountOnExit>
              <Box sx={{ height: '100%' }}>
                {isMobile ? <MobileAgendaView /> : <WeekView />}
              </Box>
            </Slide>
          </Box>
  
          {/* Enhanced Event Detail Modal */}
          <Modal
            open={!!selectedEvent}
            onClose={handleCloseModal}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: 2,
            }}
          >
            <Fade in={!!selectedEvent}>
              <Card sx={{ 
                maxWidth: 480, 
                width: '100%', 
                maxHeight: '80%', 
                overflow: 'auto',
                boxShadow: 8,
              }}>
                {selectedEvent && (
                  <CardContent sx={{ p: 0 }}>
                    {/* Header */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      p: 3, 
                      borderBottom: '1px solid',
                      borderColor: 'grey.200',
                    }}>
                      <Box
                        sx={{
                          width: 4,
                          height: 40,
                          backgroundColor: selectedEvent.color,
                          borderRadius: 2,
                          mr: 2,
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 500,
                          fontSize: '1.25rem',
                          lineHeight: 1.2,
                          mb: 1,
                        }}>
                          {selectedEvent.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedEvent.start.toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" sx={{ color: 'text.secondary' }}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" sx={{ color: 'text.secondary' }}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
  
                    {/* Details */}
                    <Box sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <AccessTime fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                        <Typography variant="body1">
                          {selectedEvent.start.toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                          })} - {selectedEvent.end.toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                          })}
                        </Typography>
                      </Box>
  
                      {selectedEvent.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <LocationOn fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                          <Typography variant="body1">{selectedEvent.location}</Typography>
                        </Box>
                      )}
  
                      {selectedEvent.description && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                            {selectedEvent.description}
                          </Typography>
                        </>
                      )}
  
                      {selectedEvent.attendees && selectedEvent.attendees.length > 0 && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                            Attendees ({selectedEvent.attendees.length})
                          </Typography>
                          {selectedEvent.attendees.map((attendee, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Avatar sx={{ width: 32, height: 32, mr: 2, fontSize: '0.875rem' }}>
                                {attendee.split('@')[0].charAt(0).toUpperCase()}
                              </Avatar>
                              <Typography variant="body2">{attendee}</Typography>
                            </Box>
                          ))}
                        </>
                      )}
                    </Box>
                  </CardContent>
                )}
              </Card>
            </Fade>
          </Modal>
        </Paper>
      </ThemeProvider>
    );
  };
  
  export default GoogleCalendar;